import {
  REMOVE_ALERT_BEGIN,
  REMOVE_ALERT_SUCCESS,
  REMOVE_ALERT_FAILURE,
  REMOVE_ALERT_DISMISS_ERROR,
  REMOVE_ALERTS_BATCH_BEGIN,
  REMOVE_ALERTS_BATCH_SUCCESS,
  REMOVE_ALERTS_BATCH_FAILURE,
} from './constants';
import { app } from '../../../common/feathers';
import { removeFromArr } from '../../../common/ambient';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useCallback } from 'react';

const alertService = app.service('alerts');

// Helper function to remove a single alert
const removeSingleAlert = async (alert, dispatch) => {
  try {
    const res = await alertService.remove(alert._id);
    dispatch({
      type: REMOVE_ALERT_SUCCESS,
      data: alert,
    });
    return res;
  } catch (err) {
    dispatch({
      type: REMOVE_ALERT_FAILURE,
      data: {
        error: err,
        id: alert._id
      },
    });
    throw err;
  }
};

// Main function that handles both single and batch alert removal
export function removeAlert(args) {
  return async (dispatch) => {
    // Handle batch alert removal
    if (Array.isArray(args)) {
      const alertIds = args.map(alert => alert._id);
      
      dispatch({
        type: REMOVE_ALERTS_BATCH_BEGIN,
        data: alertIds
      });

      try {
        // Remove alerts sequentially to maintain order and prevent race conditions
        const results = [];
        for (const alert of args) {
          const result = await removeSingleAlert(alert, dispatch);
          results.push(result);
        }

        dispatch({
          type: REMOVE_ALERTS_BATCH_SUCCESS,
          data: args,
        });

        return results;

      } catch (err) {
        dispatch({
          type: REMOVE_ALERTS_BATCH_FAILURE,
          data: {
            error: err,
            ids: alertIds
          },
        });
        throw err;
      }
    } else {
      // Handle single alert removal
      dispatch({
        type: REMOVE_ALERT_BEGIN,
        data: args._id
      });

      try {
        const result = await removeSingleAlert(args, dispatch);
        return result;
      } catch (err) {
        // Error is already dispatched in removeSingleAlert
        throw err;
      }
    }
  };
}

export const useRemoveAlert = () => {
  const dispatch = useDispatch();
  const {
    removeAlertPending,
    removeAlertError,
    removeAlertsInProgress,
    removeAlertsFailed,
    batchRemovePending,
  } = useSelector(
    state => ({
      removeAlertPending: state.alert.removeAlertPending,
      removeAlertError: state.alert.removeAlertError,
      removeAlertsInProgress: state.alert.removeAlertsInProgress || [],
      removeAlertsFailed: state.alert.removeAlertsFailed || [],
      batchRemovePending: state.alert.batchRemovePending,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    data => dispatch(removeAlert(data)),
    [dispatch],
  );

  const boundDismissError = useCallback(
    () => dispatch(dismissRemoveAlertError()),
    [dispatch],
  );

  return {
    removeAlert: boundAction,
    removeAlertPending,
    removeAlertError,
    removeAlertsInProgress,
    removeAlertsFailed,
    batchRemovePending,
    dismissRemoveAlertError: boundDismissError,
  };
};

export function dismissRemoveAlertError() {
  return {
    type: REMOVE_ALERT_DISMISS_ERROR,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case REMOVE_ALERTS_BATCH_BEGIN:
      return {
        ...state,
        batchRemovePending: true,
        removeAlertError: null,
        removeAlertsInProgress: [...(state.removeAlertsInProgress || []), ...action.data],
      };

    case REMOVE_ALERTS_BATCH_SUCCESS:
      return {
        ...state,
        batchRemovePending: false,
        removeAlertError: null,
        alerts: state.alerts.filter(alert => 
          !action.data.find(removedAlert => removedAlert._id === alert._id)
        ),
        removeAlertsInProgress: (state.removeAlertsInProgress || []).filter(
          id => !action.data.find(alert => alert._id === id)
        ),
        removeAlertsFailed: (state.removeAlertsFailed || []).filter(
          id => !action.data.find(alert => alert._id === id)
        ),
      };

    case REMOVE_ALERTS_BATCH_FAILURE:
      return {
        ...state,
        batchRemovePending: false,
        removeAlertError: action.data.error,
        removeAlertsFailed: [...(state.removeAlertsFailed || []), ...action.data.ids],
      };

    case REMOVE_ALERT_BEGIN:
      return {
        ...state,
        removeAlertPending: true,
        removeAlertError: null,
        removeAlertsInProgress: action.data ? 
          [...(state.removeAlertsInProgress || []), action.data] : 
          (state.removeAlertsInProgress || []),
      };

    case REMOVE_ALERT_SUCCESS:
      return {
        ...state,
        removeAlertPending: false,
        removeAlertError: null,
        alerts: removeFromArr(action.data, state.alerts),
        removeAlertsInProgress: (state.removeAlertsInProgress || []).filter(
          id => id !== action.data._id
        ),
        removeAlertsFailed: (state.removeAlertsFailed || []).filter(
          id => id !== action.data._id
        ),
      };

    case REMOVE_ALERT_FAILURE:
      return {
        ...state,
        removeAlertPending: false,
        removeAlertError: action.data.error,
        removeAlertsFailed: action.data.id ? 
          [...(state.removeAlertsFailed || []), action.data.id] :
          (state.removeAlertsFailed || []),
      };

    case REMOVE_ALERT_DISMISS_ERROR:
      return {
        ...state,
        removeAlertError: null,
      };

    default:
      return state;
  }
}